import React, { useEffect, useRef, useState } from "react"
import { languageMap } from "../../sanity/languages"
import cn from "classnames"
import s from "./LanguageSelector.module.scss"
import Icon, { IconType } from "src/components/icon/Icon"
import { createFocusTrap } from "focus-trap"
import { PageReference } from "../../sanity/types"
import buildLink from "../../sanity/buildLink"
import { Container } from "src/layout/container/Container"
import { useSettings } from "src/layout/page/settingsContext"
import fetchy from "../../api/fetch"
import { getCookie } from "src/utils"
import { getCallToActionTrackingHandler } from "../call-to-action/CallToAction"

declare global {
    interface Window {
        googleTranslateElementInit?: () => void
        google: any
    }
}

interface LanguageSelectorProps {
    pageReferences?: PageReference[]
}

const langOrder = Object.keys(languageMap)

export default function LanguageSelector({ pageReferences }: LanguageSelectorProps) {
    const [open, setOpen] = useState(false)
    const { translations: t, settings, lang, segment } = useSettings()
    const [isSwedishEnglish, setIsSwedishEnglish] = useState(false)
    const ref = useRef<HTMLDivElement>()

    const referenceOverride = (pageReferences || []).reduce((col, pageRef) => {
        col[pageRef.lang] = buildLink(pageRef.page)
        return col
    }, {})

    const languageSettings = settings
        .reduce(
            (acc, setting, i, all) => {
                // Current language + segment
                if (setting.lang === lang && setting.segment === segment) {
                    acc.push(setting)
                    return acc
                }

                // Other languages + same segment
                if (setting.lang !== lang && setting.segment === segment) {
                    acc.push(setting)
                    return acc
                }

                // Other languages + other segment, and no matching segment
                if (
                    setting.lang !== lang &&
                    setting.segment !== segment &&
                    !all.find((e) => e.lang === setting.lang && e.segment === segment)
                ) {
                    acc.push(setting)
                    return acc
                }

                return acc
            },
            [] as typeof settings,
        )
        .sort((a, b) => langOrder.indexOf(a.lang) - langOrder.indexOf(b.lang))

    useEffect(() => {
        if (typeof window !== "undefined") {
            const isSwedishEnglishCookie = getCookie("isSwedishEnglish")
            setIsSwedishEnglish(isSwedishEnglishCookie === "true" && lang === "se")
        }
    }, [])
    useEffect(() => {
        if (typeof window !== "undefined") {
            if (location.search.includes("hl=en") && lang === "se") {
                setIsSwedishEnglish(true)
                document.cookie = "isSwedishEnglish=true; path=/; domain=" + location.hostname
            }
        }
    }, [])
    useEffect(() => {
        if (typeof window !== "undefined") {
            const isAlreadyInstalled = document.getElementById("google-translate-script")
            if (!isAlreadyInstalled && isSwedishEnglish && lang === "se") {
                ;(function () {
                    // Set Google Translate cookie to force Swedish → English
                    document.cookie = "googtrans=/sv/en;path=/;domain=" + location.hostname

                    // Inject the Google Translate script
                    const s = document.createElement("script")
                    s.id = "google-translate-script"
                    s.src = "//translate.google.com/translate_a/element.js?cb=googleTranslateElementInit"
                    document.body.appendChild(s)

                    window.googleTranslateElementInit = function () {
                        new window.google.translate.TranslateElement({
                            pageLanguage: "sv",
                            autoDisplay: false,
                        })
                    }

                    // Setup disclaimer popup, that shows that the page is currently machine translated
                    const disclaimerId = "google-translate-disclaimer"
                    let disclaimer = document.getElementById(disclaimerId)
                    if (!disclaimer) {
                        disclaimer = document.createElement("div")
                        disclaimer.id = disclaimerId
                        disclaimer.style.position = "fixed"
                        disclaimer.style.bottom = "0"
                        disclaimer.style.left = "0"
                        disclaimer.style.width = "100%"
                        disclaimer.style.backgroundColor = "var(--gray-30)"
                        disclaimer.style.color = "var(--gray-100)"
                        disclaimer.style.padding = "10px"
                        disclaimer.style.zIndex = "10000"
                        disclaimer.style.fontSize = "11px"
                        disclaimer.style.lineHeight = "130%"
                        disclaimer.style.textAlign = "center"
                        disclaimer.style.boxSizing = "border-box"
                        disclaimer.innerHTML =
                            "This page is machine translated from Swedish to English for your convenience. For accurate information, please refer to the "
                        const changeLanguageToSwedishButton = document.createElement("span")
                        changeLanguageToSwedishButton.style.textDecoration = "underline"
                        changeLanguageToSwedishButton.style.cursor = "pointer"
                        changeLanguageToSwedishButton.innerText = "original Swedish version here."
                        changeLanguageToSwedishButton.onclick = function () {
                            document.cookie = "isSwedishEnglish=false; path=/; domain=" + location.hostname
                            location.href = location.href.replace("hl=en", "")
                        }
                        disclaimer.appendChild(changeLanguageToSwedishButton)

                        document.body.appendChild(disclaimer)
                    }
                })()
            }
        }
    }, [isSwedishEnglish, lang])

    useEffect(() => {
        if (open) {
            document.body.style.overflow = "hidden"
        } else {
            document.body.style.overflow = "auto"
        }
    }, [open])

    useEffect(() => {
        if (open) {
            const trap = createFocusTrap(ref.current, {
                onDeactivate: () => setOpen(false),
            })

            trap.activate()

            return () => {
                trap.deactivate()
            }
        }
    }, [open])

    const flags = {
        "dk": IconType.DK_FLAG,
        "no": IconType.NO_FLAG,
        "se": IconType.SE_FLAG,
        "en": IconType.DK_FLAG,
    }

    const changeLocale = (locale: string) => {
        document.cookie = "isSwedishEnglish=false; path=/; domain=" + location.hostname
        setIsSwedishEnglish(false)

        fetchy.post("/api/set-locale", {
            locale,
        })

        if (isSwedishEnglish) location.href = location.href.replace("hl=en", "")
    }

    return (
        <>
            <div className={s["language-selector"]}>
                <button
                    className={cn(s["language-selector__button"], "label-100")}
                    onClick={(event) => {
                        setOpen((i) => !i)
                        getCallToActionTrackingHandler({
                            type: "link",
                            linkType: "event",
                            event: "open-language-selector",
                            title: "Open language selector",
                        })(event)
                    }}
                >
                    <Icon type={IconType.GLOBE} className={s["language-selector__button__globe"]} />
                    {languageMap[lang].name} <span className="visually-hidden">({t?.languages.change})</span>
                    <Icon type={IconType.CHEVRON_DOWN} className={s["language-selector__button__chevron"]} />
                </button>
            </div>
            <div
                ref={ref}
                style={{
                    display: open ? "flex" : "none",
                    opacity: open ? "1" : "0",
                    transition: "opacity 0.3s ease",
                    pointerEvents: open ? "all" : "none",
                }}
                className={cn(s["language-selector__popup"])}
                data-module-name="language-selector"
            >
                <Container>
                    <h2 className={cn(s["language-selector__title"], "heading-300")}>{t?.languages.selectLanguage}</h2>

                    <button
                        onClick={() => setOpen(false)}
                        className={cn(s["language-selector__close"], "button", "button--close")}
                    >
                        <Icon type={IconType.X} style={{ color: "black" }} />
                        <span className="visually-hidden">{t?.languages.close}</span>
                    </button>

                    <ul className={s["language-selector__list"]} role="menu">
                        {languageSettings
                            .filter((each) => each.lang)
                            .map((each, idx) => {
                                // needs to be hard refresh for lang html prop to update properly
                                return (
                                    <React.Fragment key={[each.lang, idx].join("-")}>
                                        <li className={cn(s["language-selector__item"])}>
                                            <a
                                                className={cn(s["language-selector__link"], "label-100", {
                                                    [s["language-selector__link--active"]]:
                                                        each.lang === lang && !isSwedishEnglish,
                                                })}
                                                onMouseDown={(event) => {
                                                    changeLocale(each.lang)
                                                    getCallToActionTrackingHandler({
                                                        type: "link",
                                                        linkType: "event",
                                                        event: "change-language",
                                                        title: "Change language to " + each.lang,
                                                    })(event)
                                                }}
                                                hrefLang={each.lang}
                                                role="menuitem"
                                                href={
                                                    referenceOverride[each.lang]
                                                        ? referenceOverride[each.lang]
                                                        : buildLink(each.link)
                                                }
                                            >
                                                <Icon type={flags[each.lang || "dk"]} /> {languageMap[each.lang]?.name}
                                                {each.lang === lang && !isSwedishEnglish && (
                                                    <Icon
                                                        style={{ marginLeft: "auto", marginRight: 0 }}
                                                        type={IconType.CIRCLED_CHECK}
                                                    />
                                                )}
                                            </a>
                                        </li>
                                        {each.lang === "se" && (
                                            <>
                                                <li className={cn(s["language-selector__item"])}>
                                                    <a
                                                        className={cn(s["language-selector__link"], "label-100", {
                                                            [s["language-selector__link--active"]]: isSwedishEnglish,
                                                        })}
                                                        onMouseDown={(event) => {
                                                            document.cookie =
                                                                "isSwedishEnglish=true; path=/; domain=" +
                                                                location.hostname
                                                            getCallToActionTrackingHandler({
                                                                type: "link",
                                                                linkType: "event",
                                                                event: "change-language",
                                                                title: "Change language to se-en",
                                                            })(event)
                                                        }}
                                                        onMouseUp={() => {
                                                            setIsSwedishEnglish(true)
                                                            setTimeout(() => {
                                                                if (lang === "se") setOpen(false)
                                                            }, 100)
                                                        }}
                                                        hrefLang="se-en"
                                                        role="menuitem"
                                                        href={
                                                            referenceOverride[each.lang]
                                                                ? referenceOverride[each.lang]
                                                                : buildLink(each.link)
                                                        }
                                                    >
                                                        <Icon type={flags["se"]} /> Sverige (English)
                                                        {isSwedishEnglish && (
                                                            <Icon
                                                                style={{ marginLeft: "auto", marginRight: 0 }}
                                                                type={IconType.CIRCLED_CHECK}
                                                            />
                                                        )}
                                                    </a>
                                                </li>
                                                <style
                                                    dangerouslySetInnerHTML={{
                                                        __html: `
                                                .skiptranslate {
                                                    display: none !important;
                                                }
                                                body {
                                                    top: 0 !important;
                                                }
                                                font {
                                                    background-color: unset !important;
                                                    box-shadow: unset !important;
                                                }
                                            `,
                                                    }}
                                                />
                                            </>
                                        )}
                                    </React.Fragment>
                                )
                            })}
                    </ul>
                </Container>
            </div>
        </>
    )
}
